/**
 * 🔴 #367 — A DIVERGÊNCIA ACEITA, na cotação (decisões do Matheus, 19/09/2026).
 *
 * "Mantenho 29 porque comprei caixa fechada": o Compras aceita a diferença com o levantamento, com MOTIVO OBRIGATÓRIO. O ALARME some;
 * a DIFERENÇA continua na linha, sem alarme ("29 na cotação #10 · 26 no levantamento: divergência aceita por Gabriel em 17/09 (…)").
 * O que sai é o pedido de ação, não o fato. Se qualquer lado mudar depois, o alarme volta (o servidor decide). Desfazer tem trilha.
 */
import { useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErroCampo,
  HelpTip,
  Label,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { errMsg } from '@/lib/api-error';

/** O mesmo mínimo do servidor (`MOTIVO_MINIMO`): "ok" não explica a divergência para quem ler depois. */
const MOTIVO_MINIMO = 10;

interface Acao<T> {
  mutate: (v: T, o?: { onSuccess?: () => void; onError?: (e: unknown) => void }) => void;
  isPending: boolean;
}

export function BotaoManter({
  itemId,
  manter,
  aceitar,
}: {
  itemId: string;
  manter: number;
  aceitar: Acao<{ itemId: string; motivo: string }>;
}) {
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const campo = useRef<HTMLTextAreaElement>(null);

  function confirmar() {
    if (motivo.trim().length < MOTIVO_MINIMO) {
      setErro(`Escreva o motivo (pelo menos ${MOTIVO_MINIMO} letras): é ele que explica a diferença para quem ler depois.`);
      campo.current?.focus();
      return;
    }
    aceitar.mutate(
      { itemId, motivo: motivo.trim() },
      {
        onSuccess: () => {
          setAberto(false);
          setMotivo('');
          toast.show(`Divergência aceita: fica ${manter}. O aviso saiu; a diferença continua na linha.`, { variant: 'success' });
        },
        onError: (e) => {
          setErro(errMsg(e) ?? 'Não deu para aceitar. Tente de novo.');
          campo.current?.focus();
        },
      },
    );
  }

  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          size="sm"
          variant="secondary"
          className="min-h-[44px] sm:min-h-0"
          onClick={() => {
            setErro(null);
            setAberto(true);
          }}
          data-testid={`manter-divergencia-${itemId}`}
        >
          Manter: {manter}
        </Button>
        <HelpTip texto="Use quando a diferença é de propósito (caixa fechada, perda prevista, o cliente fornece parte). Você escreve o motivo, o aviso sai e a diferença continua escrita na linha e no levantamento. Se o levantamento ou a cotação mudarem depois, o aviso volta." />
      </span>
      {aberto && (
        <Dialog open onOpenChange={(o) => { if (!o) setAberto(false); }}>
          <DialogHeader>
            <DialogTitle>Manter {manter} e aceitar a diferença?</DialogTitle>
            <DialogDescription>
              O aviso sai. A diferença continua escrita na linha, com o seu nome, a data e o motivo, e o levantamento da obra também mostra.
              Se o levantamento ou a cotação mudarem depois, o aviso volta.
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            <Label htmlFor={`motivo-divergencia-${itemId}`}>Motivo (obrigatório)</Label>
            <textarea
              id={`motivo-divergencia-${itemId}`}
              ref={campo}
              value={motivo}
              maxLength={500}
              rows={3}
              placeholder="Ex.: comprei caixa fechada com 29"
              aria-invalid={!!erro}
              aria-describedby={erro ? `motivo-divergencia-${itemId}-erro` : undefined}
              onChange={(e) => {
                setMotivo(e.target.value);
                if (erro) setErro(null);
              }}
              className="mt-1 w-full rounded-md border border-line bg-paper-surface p-2 text-base text-ink-1 dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1 sm:text-sm"
              data-testid={`motivo-divergencia-${itemId}`}
            />
            <ErroCampo id={`motivo-divergencia-${itemId}`} mensagem={erro ?? undefined} />
          </DialogContent>
          <DialogFooter>
            <Button variant="ghost" className="min-h-[44px] sm:min-h-0" onClick={() => setAberto(false)} disabled={aceitar.isPending}>
              Voltar
            </Button>
            <Button className="min-h-[44px] sm:min-h-0" onClick={confirmar} loading={aceitar.isPending} data-testid={`confirmar-manter-${itemId}`}>
              Manter {manter}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
}

export function BotaoDesfazerAceitacao({ itemId, desfazer }: { itemId: string; desfazer: Acao<string> }) {
  const toast = useToast();
  const confirm = useConfirmDialog();
  async function clicar() {
    const ok = await confirm({
      title: 'Desfazer a aceitação?',
      message: 'O aviso de divergência volta para este item. A aceitação fica registrada na trilha.',
      confirmLabel: 'Desfazer',
    });
    if (!ok) return;
    desfazer.mutate(itemId, {
      onSuccess: () => toast.show('Aceitação desfeita: o aviso voltou.', { variant: 'success' }),
      onError: (e) => toast.show(errMsg(e) ?? 'Não deu para desfazer. Tente de novo.', { variant: 'error' }),
    });
  }
  return (
    <Button
      size="sm"
      variant="ghost"
      className="min-h-[44px] sm:min-h-0"
      loading={desfazer.isPending}
      onClick={clicar}
      data-testid={`desfazer-aceitacao-${itemId}`}
    >
      Desfazer aceitação
    </Button>
  );
}
